'use client';

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {AlertTriangle} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getMasterDataApi} from '@/matic/features/master-data/api/master-data-api';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';
import {useToast} from '@/matic/components/ui/toast';
import type {ObjectTypeField} from '@/matic/features/master-data/types/master-data-types';

type AddFieldFormProps = {
  typeId: string;
  activeFields: ObjectTypeField[];
  onFieldAdded?: (field: ObjectTypeField) => void;
};

type RestrictedFieldType = 'text' | 'number' | 'date' | 'boolean' | 'dropdown';

const RESTRICTED_TYPES: RestrictedFieldType[] = [
  'text',
  'number',
  'date',
  'boolean',
  'dropdown',
];

const TYPE_LABELS: Record<RestrictedFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Boolean',
  dropdown: 'Dropdown',
};

/**
 * Inline "+ Add field" affordance — adds a new attribute to the ObjectType on
 * the fly so a value can be captured without first visiting "Manage type".
 * MDM-generic: hosted by both the Library type-management dialog and the shared
 * record edit form.
 */
export function AddFieldForm({
  typeId,
  activeFields,
  onFieldAdded,
}: AddFieldFormProps) {
  const queryClient = useQueryClient();
  const tenantId = useRequiredTenantId();
  const {toast} = useToast();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<RestrictedFieldType>('text');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');

  const similarField =
    label.trim().length > 1
      ? activeFields.find(
          (f) =>
            f.label.toLowerCase().includes(label.trim().toLowerCase()) ||
            label.trim().toLowerCase().includes(f.label.toLowerCase())
        )
      : undefined;

  const addFieldMutation = useMutation({
    async mutationFn() {
      const fieldKey = label
        .trim()
        .toLowerCase()
        .replaceAll(/[^a-z\d]+/g, '_')
        .replaceAll(/^_|_$/g, '')
        .slice(0, 32);
      const sortOrder =
        Math.max(0, ...activeFields.map((f) => f.sortOrder)) + 1;
      const config = fieldType === 'dropdown' ? {options} : undefined;
      return getMasterDataApi().addField(typeId, {
        label: label.trim(),
        fieldKey,
        fieldType,
        required,
        sortOrder,
        ...(config && {config}),
      });
    },
    async onSuccess(field) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectType(typeId, tenantId),
        }),
        queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectTypeLists(),
        }),
      ]);
      toast({title: `Field "${field.label}" added`, variant: 'success'});
      setLabel('');
      setFieldType('text');
      setRequired(false);
      setOptions([]);
      setNewOption('');
      setOpen(false);
      onFieldAdded?.(field);
    },
    onError() {
      toast({title: 'Failed to add field', variant: 'error'});
    },
  });

  const addOption = () => {
    const opt = newOption.trim();
    if (opt && !options.includes(opt)) {
      setOptions([...options, opt]);
      setNewOption('');
    }
  };

  const canSubmit =
    label.trim().length > 0 &&
    !addFieldMutation.isPending &&
    (fieldType !== 'dropdown' || options.length > 0);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
      >
        <span className="text-base leading-none">+</span>
        Add field
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
          New field
        </span>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setLabel('');
            setFieldType('text');
            setRequired(false);
            setOptions([]);
          }}
          className="text-xs text-neutral-400 hover:text-neutral-600"
        >
          Cancel
        </button>
      </div>

      <div className="space-y-2.5">
        {/* Label */}
        <div>
          <input
            autoFocus
            type="text"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
            }}
            placeholder="Field label"
            className="w-full rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {similarField && (
            <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              Similar to existing field: {similarField.label}
            </div>
          )}
        </div>

        {/* Type selector */}
        <div className="flex flex-wrap gap-1.5">
          {RESTRICTED_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setFieldType(t);
                if (t !== 'dropdown') setOptions([]);
              }}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                fieldType === t
                  ? 'border-primary-400 bg-primary-50 text-primary-700'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
              )}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Dropdown options */}
        {fieldType === 'dropdown' && (
          <div>
            <div className="mb-1.5 flex flex-wrap gap-1">
              {options.map((opt) => (
                <span
                  key={opt}
                  className="flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
                >
                  {opt}
                  <button
                    type="button"
                    onClick={() => {
                      setOptions(options.filter((o) => o !== opt));
                    }}
                    className="text-neutral-400 hover:text-neutral-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={newOption}
              onChange={(e) => {
                setNewOption(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addOption();
                }
              }}
              placeholder="Add option, press Enter"
              className="w-full rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        )}

        {/* Required toggle + submit */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-1.5 text-xs text-neutral-600">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => {
                setRequired(e.target.checked);
              }}
              className="h-3.5 w-3.5 rounded border-neutral-300 accent-primary-500"
            />
            Required
          </label>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              addFieldMutation.mutate();
            }}
            className="rounded bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            {addFieldMutation.isPending ? 'Adding…' : 'Add field'}
          </button>
        </div>
      </div>
    </div>
  );
}
